/**
 * Types du "fil" de la route batch (`POST /_batch`), tels que l'API les attend et les renvoie.
 * Volontairement isolés du reste : ce sont les seuls endroits où la forme exacte du protocole
 * apparaît, tout le reste du module raisonne en models.
 */

export type BatchMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Une requête du lot. `route` est ABSOLUE, préfixe de service compris (`/map/places`). */
export type BatchRequestItem = {
	method: BatchMethod;
	route: string;
	body: string | null; // Le corps est une CHAÎNE JSON, pas un objet
	headers: Record<string, string>;
};

/** La réponse correspondante. Le tableau renvoyé est parallèle à celui envoyé (même ordre). */
export type BatchResponseItem = {
	method: BatchMethod;
	route: string;
	status: number;
	body: Record<string, any> | null;
};

/**
 * Jeton de substitution : l'API remplace `[:$N.id]` par l'`id` renvoyé par la requête de rang N du
 * même lot, aussi bien dans la route que dans le corps. Seul `id` est repris pour le moment, d'où
 * les urns reconstruites à la main (voir `BatchOperation.urn`).
 */
export const batchIdPlaceholder = (index: number) => `[:$${index}.id]`;

/**
 * Ce que le manager attend d'un connector pour jouer un lot.
 *
 * Décrit, et non importé : les connectors vivent dans les projets — c'est là que se trouvent l'URL
 * du service, l'authentification et la gestion d'erreur — alors que ce module est dans le core. Le
 * contrat tient en deux membres, chaque connector capable de batch les expose.
 */
export interface BatchConnector {
	/** Le préfixe des routes du service (`/map`), le lot les attendant absolues. */
	servicePath: string;

	/**
	 * Les en-têtes du client que chaque sous-requête doit porter : rejouée en interne, elle ne voit
	 * pas ceux de l'enveloppe, dont le contexte d'évaluation des droits.
	 */
	subRequestHeaders?: Record<string, string>;

	batch(items: BatchRequestItem[], options?: { transactional?: boolean }): Promise<BatchResponseItem[]>;
}
